import { TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    DAY_PARTS,
    type DayPartKey,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import {
    COVERAGE_COLUMNS,
    type CoverageArea,
    type CoverageBucket,
    LOW_COVERAGE_THRESHOLD,
    maxCoverageCount,
} from '@apps/web/features/coverage/model/rollup_model';
import {
    cellKey,
    type CoverageSelectedCell,
    type CoverageSelection,
    isCellSelected,
    selectedCellKeys,
} from '@apps/web/features/coverage/model/selection_model';
import { useCoverageMarquee } from '@apps/web/features/coverage/model/use_coverage_marquee';
import { MarqueeOverlay } from '@apps/web/lib/marquee_selection';
import { cn } from '@apps/web/lib/utils';

const AREA_LABELS: Record<CoverageArea, string> = {
    office: 'Office',
    city: 'City',
    zip: 'ZIP',
};

const COVERAGE_PAGE_SIZE = 50;

// Sequential single-hue ramp (magnitude), light to dark. The count is
// printed in every cell so color is never the only channel; low/zero
// cells additionally carry an icon + text.
const RAMP = [
    'bg-transparent',
    'bg-blue-100 dark:bg-blue-950',
    'bg-blue-200 dark:bg-blue-900',
    'bg-blue-300 dark:bg-blue-800',
    'bg-blue-500 text-white',
    'bg-blue-600 text-white',
    'bg-blue-700 text-white',
];

function rampClass(count: number, max: number): string {
    if (count === 0) {
        return 'bg-transparent';
    }
    const step = Math.max(1, Math.ceil((count / max) * (RAMP.length - 1)));
    return RAMP[step] ?? 'bg-blue-700 text-white';
}

function partLabel(dayPart: DayPartKey): string {
    return DAY_PARTS.find((part) => part.key === dayPart)?.label ?? dayPart;
}

export function CoverageAreaPane({
    coverage,
    area,
    onAreaChange,
    onSelectCell,
    onSelectCells,
    onSelectArea,
    onClearSelection,
    selection,
    workWeekStartDay = 'monday',
    onClearFilters,
    aside,
}: {
    readonly coverage: ReadonlyArray<CoverageBucket>;
    readonly area: CoverageArea;
    readonly onAreaChange: (area: CoverageArea) => void;
    readonly onSelectCell: (
        areaKey: string,
        group: 'weekday' | 'weekend',
        dayPart: DayPartKey,
        options?: { readonly additive?: boolean },
    ) => void;
    readonly onSelectCells?: (
        cells: ReadonlyArray<CoverageSelectedCell>,
    ) => void;
    readonly onSelectArea?: (areaKey: string) => void;
    readonly onClearSelection?: () => void;
    readonly selection?: CoverageSelection;
    readonly workWeekStartDay?: WorkWeekStartDay;
    readonly onClearFilters?: () => void;
    /** Shares the table's top edge so the rail lines up with its header. */
    readonly aside?: React.ReactNode;
}) {
    const [visibleBucketCount, setVisibleBucketCount] =
        useState(COVERAGE_PAGE_SIZE);
    useEffect(() => {
        setVisibleBucketCount(COVERAGE_PAGE_SIZE);
    }, [coverage, area]);
    const max = maxCoverageCount(coverage);
    const visibleCoverage = coverage.slice(0, visibleBucketCount);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const selectedKeys = useMemo(
        () =>
            selection === undefined
                ? new Set<string>()
                : selectedCellKeys(selection),
        [selection],
    );
    const { marqueeRect, isMarqueeActive, marqueeHandlers } =
        useCoverageMarquee({
            scrollElementRef: scrollRef,
            // Headers and the label column keep ordinary text selection.
            ignoreOriginSelector: 'th, thead',
            onEmptyPress: () => onClearSelection?.(),
            onSelectIds: (ids) => {
                if (onSelectCells === undefined) {
                    return;
                }
                onSelectCells(
                    ids.flatMap((id) => {
                        const [areaKey, group, dayPart] = id.split('|');
                        return areaKey === undefined ||
                            group === undefined ||
                            dayPart === undefined
                            ? []
                            : [
                                  {
                                      areaKey,
                                      group: group as 'weekday' | 'weekend',
                                      dayPart: dayPart as DayPartKey,
                                  },
                              ];
                    }),
                );
            },
        });

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <div
                    className="border-border inline-flex overflow-hidden rounded-lg border"
                    role="group"
                    aria-label="Group coverage by"
                >
                    {(Object.keys(AREA_LABELS) as Array<CoverageArea>).map(
                        (option) => (
                            <button
                                key={option}
                                type="button"
                                aria-pressed={area === option}
                                onClick={() => onAreaChange(option)}
                                className={cn(
                                    'px-3 py-1.5 text-sm font-medium',
                                    area === option
                                        ? 'bg-primary text-primary-foreground'
                                        : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {AREA_LABELS[option]}
                            </button>
                        ),
                    )}
                </div>
                <p className="text-muted-foreground text-xs">
                    Select a cell to choose who to text.
                </p>
            </div>
            <div className="@container/coverage-layout">
                <div className="flex max-w-[1600px] flex-col items-stretch gap-4 @min-[1160px]/coverage-layout:flex-row @min-[1160px]/coverage-layout:items-start">
                    <div className="min-w-0 space-y-3">
                        {coverage.length === 0 ? (
                            <div className="border-border rounded-xl border border-dashed py-12 text-center">
                                <p className="text-muted-foreground text-sm">
                                    No coverage areas match the current filters.
                                </p>
                                {onClearFilters ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-3"
                                        onClick={onClearFilters}
                                    >
                                        Clear filters
                                    </Button>
                                ) : null}
                            </div>
                        ) : (
                            <div
                                ref={scrollRef}
                                className={cn(
                                    'relative overflow-x-auto',
                                    // Only while a rectangle is being drawn:
                                    // outside a drag, labels select normally.
                                    marqueeRect && 'select-none',
                                )}
                                {...marqueeHandlers}
                            >
                                {marqueeRect === null ? null : (
                                    <MarqueeOverlay
                                        rect={marqueeRect}
                                        testId="coverage-marquee"
                                    />
                                )}
                                <table className="min-w-[720px] border-separate border-spacing-0.5">
                                    <thead>
                                        <tr>
                                            <th scope="col" colSpan={2} />
                                            <th
                                                scope="colgroup"
                                                colSpan={4}
                                                className="text-muted-foreground border-border border-b p-1 text-xs font-semibold"
                                            >
                                                Weekdays
                                            </th>
                                            <th
                                                scope="colgroup"
                                                colSpan={4}
                                                className="text-muted-foreground border-border border-b p-1 text-xs font-semibold"
                                            >
                                                Weekend
                                            </th>
                                        </tr>
                                        <tr>
                                            <th
                                                scope="col"
                                                className="text-muted-foreground p-1 text-left text-xs font-semibold"
                                            >
                                                {AREA_LABELS[area]}
                                            </th>
                                            <th
                                                scope="col"
                                                className="text-muted-foreground p-1 text-right text-xs font-semibold"
                                            >
                                                Roster
                                            </th>
                                            {COVERAGE_COLUMNS.map((column) => (
                                                <th
                                                    key={`${column.group}-${column.dayPart}`}
                                                    scope="col"
                                                    className="text-muted-foreground p-1 text-center text-xs font-semibold"
                                                >
                                                    {partLabel(column.dayPart)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleCoverage.map((bucket) => (
                                            <tr key={bucket.areaKey}>
                                                <th
                                                    scope="row"
                                                    className="p-1 pr-3 text-left text-sm font-medium whitespace-nowrap"
                                                >
                                                    {onSelectArea ===
                                                    undefined ? (
                                                        bucket.areaLabel
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                onSelectArea(
                                                                    bucket.areaKey,
                                                                )
                                                            }
                                                            aria-label={`${bucket.areaLabel}: select to text everyone about the whole week`}
                                                            className="hover:text-primary block max-w-full truncate underline-offset-2 hover:underline"
                                                        >
                                                            {bucket.areaLabel}
                                                        </button>
                                                    )}
                                                </th>
                                                <td className="text-muted-foreground p-1 text-right text-xs whitespace-nowrap tabular-nums">
                                                    {bucket.rosterCount}
                                                </td>
                                                {bucket.cells.map((cell) => {
                                                    const { group, dayPart } =
                                                        cell.column;
                                                    const count =
                                                        cell.availableCount;
                                                    const low =
                                                        count <=
                                                        LOW_COVERAGE_THRESHOLD;
                                                    return (
                                                        <td
                                                            key={`${group}-${dayPart}`}
                                                            className="p-0"
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={(
                                                                    event,
                                                                ) => {
                                                                    if (
                                                                        isMarqueeActive
                                                                    ) {
                                                                        return;
                                                                    }
                                                                    onSelectCell(
                                                                        bucket.areaKey,
                                                                        group,
                                                                        dayPart,
                                                                        {
                                                                            additive:
                                                                                event.shiftKey,
                                                                        },
                                                                    );
                                                                }}
                                                                data-coverage-cell="true"
                                                                data-cell-id={cellKey(
                                                                    bucket.areaKey,
                                                                    group,
                                                                    dayPart,
                                                                )}
                                                                aria-label={`${bucket.areaLabel}, ${group} ${partLabel(dayPart)}: ${count} caregivers available on at least one matching day. Select to choose who to text.`}
                                                                className={cn(
                                                                    'hover:ring-primary focus-visible:ring-primary flex min-h-11 w-full min-w-18 flex-col items-center justify-center rounded-md px-1 py-1.5 hover:ring-2 focus-visible:ring-2 focus-visible:outline-none',
                                                                    rampClass(
                                                                        count,
                                                                        max,
                                                                    ),
                                                                    count ===
                                                                        0 &&
                                                                        'ring-1 ring-red-400',
                                                                    selection !==
                                                                        undefined &&
                                                                        isCellSelected(
                                                                            selection,
                                                                            bucket.areaKey,
                                                                            group,
                                                                            dayPart,
                                                                            selectedKeys,
                                                                        ) &&
                                                                        'ring-primary ring-2',
                                                                )}
                                                            >
                                                                <span className="text-sm font-semibold tabular-nums">
                                                                    {count}
                                                                </span>
                                                                {low ? (
                                                                    <span className="flex items-center gap-0.5 text-[10px] font-medium">
                                                                        <TriangleAlert
                                                                            className="size-2.5"
                                                                            aria-hidden="true"
                                                                        />
                                                                        {count ===
                                                                        0
                                                                            ? 'none'
                                                                            : 'low'}
                                                                    </span>
                                                                ) : null}
                                                            </button>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {visibleBucketCount < coverage.length ? (
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-muted-foreground text-xs">
                                    Showing {visibleBucketCount} of{' '}
                                    {coverage.length} areas
                                </p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setVisibleBucketCount((count) =>
                                            Math.min(
                                                coverage.length,
                                                count + COVERAGE_PAGE_SIZE,
                                            ),
                                        )
                                    }
                                >
                                    Show more areas
                                </Button>
                            </div>
                        ) : null}
                    </div>
                    {aside === undefined ? null : (
                        // self-stretch, because items-start would size this
                        // to the card and leave the sticky rail no travel.
                        <div className="w-full shrink-0 @min-[1160px]/coverage-layout:w-80 @min-[1160px]/coverage-layout:self-stretch">
                            {aside}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
